import { assert } from '@sama/js-core';
import { range, times } from '@sama/micro-dash';
import {
  Float32BufferAttribute,
  Uint16BufferAttribute,
  Uint8BufferAttribute,
} from 'three';

// SPC documentation
// https://bodhi.samasource.org/display/SsE/MIN-163%3A+Multi-point+cloud+support#MIN163:Multipointcloudsupport-SPCpointcloudsteps
const headerOffsets = {
  VERSION: 0, // Uint8
  FLAGS: 1, // Uint8
  FIELD_COUNT: 2, // Uint16
  // field metadata section start at byte #4, but its size is dynamic
  // so we take care of the following offsets in regards to this dynamicity
  FIELD_METADATA: 4,
};

const fieldMetadataByteSize = 4;

const fieldMetadataOffsets = {
  BYTE_SIZE_PER_ELEMENT: 0, // Uint8
  NUMBER_OF_ELEMENTS_PER_VALUE: 1, // Uint8
  ID: 2, // Uint16
};

const pointCountByteSize = 4;

enum FieldId {
  Position = 0,
  Intensity,
  SourceId,
}

export function getPointPositionsValues(content: ArrayBuffer) {
  const axisCount = 3;
  const fieldMetadata = getFieldMetadata(content, FieldId.Position);
  assert(fieldMetadata, 'There should be positions in the spc file');

  return new Float32BufferAttribute(
    new Float32Array(
      content,
      getFieldValueOffset(content, FieldId.Position),
      getPointCount(content) * axisCount,
    ),
    axisCount,
  );
}

export function getIntensityValues(content: ArrayBuffer) {
  const fieldMetadata = getFieldMetadata(content, FieldId.Intensity);
  if (fieldMetadata) {
    return new Uint16BufferAttribute(
      new Uint16Array(
        content,
        getFieldValueOffset(content, FieldId.Intensity),
        getPointCount(content) * fieldMetadata.numOfElementsPerPoint,
      ),
      1,
      true,
    );
  }

  return undefined;
}

export function getSourceIdValues(content: ArrayBuffer) {
  const fieldMetadata = getFieldMetadata(content, FieldId.SourceId);
  if (fieldMetadata) {
    // ThreeJS has a limitation that it casts Uint8 and Uint16 to floats,
    // but Uint32 is casted to int on GLSL
    // For type consistency, we are using the correct typing on the CPU,
    // but using floats on the GPU
    return new Uint8BufferAttribute(
      new Uint8Array(
        content,
        getFieldValueOffset(content, FieldId.SourceId),
        getPointCount(content) * fieldMetadata.numOfElementsPerPoint,
      ),
      1,
    );
  }

  return undefined;
}

function getPointCount(content: ArrayBuffer) {
  const dataView = new DataView(content);
  return dataView.getUint32(getPointCountOffset(content), true);
}

function getPointCountOffset(content: ArrayBuffer) {
  return (
    headerOffsets.FIELD_METADATA +
    getFieldMetadataCount(content) * fieldMetadataByteSize
  );
}

function getFieldMetadataCount(content: ArrayBuffer) {
  const dataView = new DataView(content);
  return dataView.getUint16(headerOffsets.FIELD_COUNT, true);
}

function buildFieldMetadata(content: ArrayBuffer) {
  const dataView = new DataView(content);

  return times(getFieldMetadataCount(content), (i) => {
    const offset = i * headerOffsets.FIELD_METADATA + fieldMetadataByteSize;

    const id = dataView.getUint8(offset + fieldMetadataOffsets.ID);
    const count = dataView.getUint8(
      offset + fieldMetadataOffsets.NUMBER_OF_ELEMENTS_PER_VALUE,
    );
    const byteSize = String.fromCharCode(
      dataView.getUint8(offset + fieldMetadataOffsets.BYTE_SIZE_PER_ELEMENT),
    );

    return {
      id,
      numOfElementsPerPoint: count,
      typedArray: getTypedArrayConstructorFromFormatCharacter(byteSize),
    };
  });
}

function getFieldMetadata(content: ArrayBuffer, fieldId: FieldId) {
  return buildFieldMetadata(content).find((value) => value.id === fieldId);
}

function getFieldValueOffset(content: ArrayBuffer, fieldId: FieldId) {
  const fieldMetadata = buildFieldMetadata(content);
  const fieldMetadataIndex = fieldMetadata.map((d) => d.id).indexOf(fieldId);
  const pointCount = getPointCount(content);

  let offset =
    headerOffsets.FIELD_METADATA +
    fieldMetadata.length * fieldMetadataByteSize +
    pointCountByteSize;
  for (const index of range(0, fieldMetadataIndex)) {
    const { numOfElementsPerPoint, typedArray } = fieldMetadata[index];
    offset += pointCount * numOfElementsPerPoint * typedArray.BYTES_PER_ELEMENT;
  }

  return offset;
}

// https://docs.python.org/3/library/struct.html#format-characters
// eslint-disable-next-line complexity -- is needed
function getTypedArrayConstructorFromFormatCharacter(character: string) {
  switch (character) {
    case 'b':
      return Int8Array;
    case 'B':
      return Uint8Array;
    case 'h':
      return Int16Array;
    case 'H':
      return Uint16Array;
    case 'i':
      return Int32Array;
    case 'I':
      return Uint32Array;
    case 'f':
      return Float32Array;
    default:
      throw new Error('Unknown format character');
  }
}
